import { BaseSyntheticEvent } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';

import useUpdateSystemTemplateMutation from 'components/domain/automation/systemTemplates/useUpdateSystemTemplateMutation';
import { FormContextParts } from 'components/forms/form/Form.types';
import useNotify from 'hooks/useNotify';
import { BaseErrorResponse } from 'models/_base';
import {
  SystemTemplate,
  UpdateSystemTemplatePayload,
} from 'models/automation/SystemTemplates';
import { getErrorMessage } from 'utils/axiosExceptionUtils';
import { typedEntries } from 'utils/funcs';
import { SystemTemplateEditModalFormValues } from './SystemTemplateEditModal.schema';

const useSystemTemplateFormSubmit = ({
  closeModal,
  systemTemplate,
}: {
  closeModal: () => void;
  systemTemplate: SystemTemplate | undefined;
}) => {
  const { t } = useTranslation();
  const { notifyError, notifySuccess } = useNotify();

  const onError = (error: BaseErrorResponse) => {
    notifyError(
      getErrorMessage({
        apiErrorCode: error.response?.data?.apiErrorCode,
        apiErrorMessage: error.response?.data?.apiErrorMessage ?? '',
        baseKey: 'pageAutomations.systemTemplates.apiErrorCodes.',
        t,
      }),
    );
  };

  const onSuccess = () => {
    closeModal();
    notifySuccess(t('pageAutomations.systemTemplates.modal.success'));
  };

  const { mutateAsync: updateSystemTemplate } =
    useUpdateSystemTemplateMutation();

  const onSubmit = async (
    formValues: SystemTemplateEditModalFormValues,
    _event: BaseSyntheticEvent | undefined,
    { reset }: FormContextParts<SystemTemplateEditModalFormValues>,
  ) => {
    if (!systemTemplate) {
      return;
    }

    const { locales, sendCopyToAgency } = formValues;

    const previousContents = typedEntries(systemTemplate.locales)
      .flatMap(([locale, content]) => {
        if (!content) {
          return [];
        }

        return [
          {
            locale,
            content: { content: content.content, subject: content.subject },
          },
        ];
      })
      .reduce((res, { locale, content }) => {
        return { ...res, [locale]: content };
      }, {} as UpdateSystemTemplatePayload['contents']);

    try {
      await updateSystemTemplate(
        {
          key: systemTemplate.key,
          contents: {
            ...previousContents,
            ...typedEntries(locales).reduce((acc, [lang, data]) => {
              if (!data) {
                return acc;
              }

              return {
                ...acc,
                [lang]: {
                  content: data.content_htmlContent,
                  subject: data.subject,
                },
              };
            }, {} as UpdateSystemTemplatePayload['contents']),
          },
          sendCopyToAgency: !!sendCopyToAgency,
        },
        {
          onSuccess: () => {
            // Reset needs to be done within `flushSync` so that when closing
            // the modal we don't see the dirty check modal appearing.
            flushSync(() => reset(formValues, { keepDirty: false }));
            onSuccess();
          },
        },
      );
    } catch (error) {
      onError(error as BaseErrorResponse);
    }
  };

  return { onSubmit };
};

export default useSystemTemplateFormSubmit;
